<template>
  <div class="container-fluid p-0 h-100">
    <!-- Alert Messages -->
    <div
      v-if="alert.status === 'success'"
      class="alert alert-success alert-dismissible fade show"
      role="alert"
    >
      {{ alert.message }}
      <button
        type="button"
        class="close"
        @click="alert.status = ''"
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
    <div
      v-if="alert.status === 'error'"
      class="alert alert-danger alert-dismissible fade show"
      role="alert"
    >
      {{ alert.message }}
      <button
        type="button"
        class="close"
        @click="alert.status = ''"
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
    <div
      v-if="alert.status === 'info'"
      class="alert alert-info alert-dismissible fade show"
      role="alert"
    >
      {{ alert.message }}
      <button
        type="button"
        class="close"
        @click="alert.status = ''"
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3>Order Pull Schedules</h3>
      <div>
        <button class="btn btn-secondary mr-2" @click="refreshData">
          <i class="fas fa-sync-alt"></i> Refresh
        </button>
        <button class="btn btn-primary" @click="showNewEventForm">
          Add New Schedule
        </button>
      </div>
    </div>

    <div class="calendar-container">
      <!-- Loading overlay -->
      <div v-if="loading" class="loading-overlay">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>

      <!-- Calendar Header -->
      <div class="calendar-header">
        <div class="date-navigation">
          <button class="btn btn-outline-secondary" @click="previousDay">
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="date-picker-container">
            <input
              type="date"
              v-model="selectedDate"
              class="form-control date-picker"
              @change="onDateChange"
            />
            <h4 class="date-display mb-0">{{ formatDate(selectedDate) }}</h4>
          </div>
          <button class="btn btn-outline-secondary" @click="nextDay">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
        <div class="header-filters">
          <div class="filter-group">
            <label for="reportTypeFilter" class="filter-label"
              >Report Type:</label
            >
            <select
              id="reportTypeFilter"
              v-model="selectedReportType"
              class="form-control report-type-selector"
              @change="onReportTypeChange"
            >
              <option value="">All Types</option>
              <option value="REGULAR_REPORT">Regular Reports</option>
              <option value="ERROR_CHECK_REPORT">Error Check Reports</option>
              <option value="ORDER_REPORT_SAME_DAY">Same Day Reports</option>
              <option value="ORDER_REPORT_NEXT_DAY">Next Day Reports</option>
              <option value="ORDER_REPORT_V_DAY">Specific Day Reports</option>
              <option value="LAKEWOOD_REPORT">Lakewood Reports</option>
              <option value="ORDER_REPORT_MAXIMUM">Maximum Reports</option>
              <option value="NOT_PULLED_REPORT">Not Pulled Reports</option>
              <option value="NOT_SENT_REPORT">Not Sent Reports</option>
              <option value="ERROR_REPORT">Error Reports</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Calendar Body -->
      <div class="calendar-body">
        <!-- Time Column -->
        <div class="time-column">
          <div class="time-header">Time</div>
          <div
            v-for="hour in timeSlots"
            :key="hour.time"
            class="time-slot"
            :style="{ height: '120px' }"
          >
            <span class="time-label">{{ hour.display }}</span>
          </div>
        </div>

        <!-- Events Column -->
        <div class="events-column">
          <div class="events-header">Events</div>
          <div
            class="events-container"
            :style="{ height: timeSlots.length * 120 + 'px' }"
          >
            <!-- Time Grid Lines -->
            <div
              v-for="(hour, index) in timeSlots"
              :key="'grid-' + hour.time"
              class="time-grid-line"
              :style="{ top: index * 120 + 'px' }"
            ></div>

            <!-- Current Time Line -->
            <div
              v-if="showCurrentTimeLine"
              class="current-time-line"
              :style="{ top: currentTimePosition + 'px' }"
            >
              <div class="current-time-indicator"></div>
            </div>

            <!-- Events -->
            <div
              v-for="event in eventsForSelectedDate"
              :key="event.id"
              class="event-item"
              :class="[
                'event-type-' + event.reportType,
                event.status === 'inactive' ? 'event-inactive' : '',
                event.status === 'disabled' ? 'event-disabled' : ''
              ]"
              :style="getEventStyle(event)"
              @click="selectEvent(event)"
            >
              <div class="event-content">
                <div class="event-title">{{ event.reportName }}</div>
                <div class="event-type">
                  {{ formatReportType(event.reportType) }}
                  <span class="event-status" v-if="getEventStatus(event)">
                    - {{ getEventStatus(event) }}
                  </span>
                </div>
                <div class="event-time">{{ formatEventTime(event) }}</div>
                <button
                  class="event-menu-btn"
                  @click.stop="showMenu($event, event)"
                >
                  ⋮
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Floating event action menu -->
      <div
        class="event-menu"
        v-if="openMenuId !== null"
        :style="{
          top: menuPosition.top + 'px',
          left: menuPosition.left + 'px'
        }"
      >
        <div @click="editEvent(currentEvent)">Edit</div>
        <div @click="editThisDayEvent(currentEvent)">Edit for this day</div>
        <div @click="manuallyGenReport(currentEvent)">Manually Run</div>
        <div
          @click="enableEvent(currentEvent)"
          v-if="
            currentEvent &&
              getScheduleState(currentEvent.scheduleId) === 'DISABLED'
          "
        >
          Enable
        </div>
        <div
          @click="disableEvent(currentEvent)"
          v-if="
            currentEvent &&
              getScheduleState(currentEvent.scheduleId) === 'ENABLED'
          "
        >
          Disable all days
        </div>
        <div
          @click="disableThisDayEvent(currentEvent)"
          v-if="
            currentEvent &&
              getScheduleState(currentEvent.scheduleId) === 'ENABLED' &&
              currentEvent.isDisabledThisDay === false
          "
        >
          Disable this day
        </div>
        <div
          @click="enableThisDayEvent(currentEvent)"
          v-if="currentEvent && currentEvent.isDisabledThisDay === true"
        >
          Enable this day
        </div>
        <!-- <div @click="deleteEvent(currentEvent)" class="text-danger">Delete</div> -->
      </div>

      <!-- New Event Form Modal -->
      <NewScheduleForm
        v-if="showingNewEventForm"
        :selected-date="selectedDate"
        :consolidate-order-opt="consolidateOrderOpt"
        :sort-header-name-opt="sortHeaderNameOpt"
        :additional-report-types-opt="additionalReportTypesOpt"
        :additional-file-types-opt="additionalFileTypesOpt"
        @save="saveNewEvent"
        @cancel="cancelNewEvent"
        @error="displayMessage('error', $event)"
      />

      <!-- Edit Event Form Modal -->
      <div
        v-if="showingEditEventForm"
        class="event-form-overlay"
        @click.self="cancelEditEvent"
      >
        <div class="event-form-container edit-form">
          <div
            class="alert alert-success alert-dismissible fade show"
            v-if="editAlert.status === 'success'"
            role="alert"
          >
            {{ editAlert.message }}
            <button
              type="button"
              class="close"
              @click="editAlert.status = ''"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div
            class="alert alert-danger alert-dismissible fade show"
            v-if="editAlert.status === 'error'"
            role="alert"
          >
            {{ editAlert.message }}
            <button
              type="button"
              class="close"
              @click="editAlert.status = ''"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <h4><strong>Edit</strong> Pull Schedule</h4>
          <p class="mt-4 mb-0">Report name and report type are required</p>

          <div class="form-card mt-3">
            <div class="report-type-name-grid">
              <div class="report-type-col">
                <div class="form-group">
                  <label class="report-label">Report type</label>
                  <select
                    v-model="editedEvent.reportType"
                    class="form-control input-height"
                  >
                    <option value="">Select an option</option>
                    <option value="ORDER_REPORT_SAME_DAY">Same day</option>
                    <option value="ORDER_REPORT_NEXT_DAY">Next day</option>
                    <option value="ORDER_REPORT_V_DAY">Specific day</option>
                    <option value="LAKEWOOD_REPORT">Lakewood Reports</option>
                    <option value="ORDER_REPORT_MAXIMUM">Maximum Report</option>
                    <option value="NOT_PULLED_REPORT">Not Pulled Report</option>
                    <option value="NOT_SENT_REPORT">Not Sent Reports</option>
                    <option value="ERROR_REPORT">Error Report</option>
                  </select>
                </div>
              </div>
              <div class="report-name-col">
                <div class="form-group">
                  <label class="report-label">Report name</label>
                  <input
                    v-model="editedEvent.reportName"
                    type="text"
                    placeholder="ORDER_REPORT_EXAMPLE"
                    class="form-control input-height upper"
                  />
                  <div class="input-box">
                    <p v-show="checkEditRepName" class="input-alert mb-0 mt-0">
                      Special characters are not allowed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="report-label">Delivery Window Restrict</label>
              <div class="checkbox-wrapper">
                <input
                  type="checkbox"
                  v-model="editedEvent.deliveryWindowRestrict"
                  class="checkbox-input"
                  id="editDeliveryWindowRestrict"
                />
                <label class="checkbox-label" for="editDeliveryWindowRestrict"
                  >Enable delivery window restriction</label
                >
              </div>
            </div>

            <div class="form-group">
              <label class="report-label">
                Regions
                <span
                  >(Do not need to select for Error Report, Not Pulled
                  Report)</span
                >
              </label>
              <div class="row">
                <div
                  v-for="(region, index) in regionsOpt"
                  :key="index"
                  class="col-md-6 col-lg-4 col-6"
                >
                  <div class="checkbox-wrapper">
                    <input
                      type="checkbox"
                      :value="region.value"
                      v-model="editedEvent.deliveryRegion"
                      class="checkbox-input"
                      :id="'editRegion' + index"
                    />
                    <label class="checkbox-label" :for="'editRegion' + index">
                      {{ region.text }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="form-group"
              v-if="editedEvent.reportType === 'ORDER_REPORT_V_DAY'"
            >
              <label class="report-label">Delivery date</label>
              <input
                type="date"
                v-model="editedEvent.deliveryDate"
                class="form-control"
              />
            </div>

            <div class="form-group">
              <label class="report-label">Description</label>
              <input
                v-model="editedEvent.description"
                type="text"
                placeholder="Description"
                class="form-control input-height upper"
              />
            </div>

            <div class="delivery-time-tag-grid">
              <div class="delivery-time-col">
                <div class="form-group">
                  <label class="report-label"
                    >Default Delivery Start Time(12:00PM)</label
                  >
                  <input
                    v-model="editedEvent.defaultDeliveryStartTime"
                    type="text"
                    placeholder="Default Delivery Start Time(12:00PM)"
                    class="form-control input-height upper"
                  />
                </div>
              </div>
              <div class="skipped-tag-col">
                <div class="form-group">
                  <label class="report-label">Skipped Tag</label>
                  <input
                    v-model="editedEvent.skippedTag"
                    type="text"
                    placeholder="Please input tag name"
                    class="form-control input-height upper"
                  />
                </div>
              </div>
            </div>

            <div class="consolidate-sort-grid">
              <div class="consolidate-col">
                <div class="form-group">
                  <label class="report-label">Consolidate Order</label>
                  <select
                    v-model="editedEvent.consolidateOrder"
                    class="form-control input-height"
                  >
                    <option
                      v-for="(optType, index) in consolidateOrderOpt"
                      :key="index"
                      :value="optType.value"
                    >
                      {{ optType.text }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="sort-col">
                <div class="form-group">
                  <label class="report-label">Sort by Header Name</label>
                  <select
                    v-model="editedEvent.sortHeaderName"
                    class="form-control input-height"
                  >
                    <option
                      v-for="(optType, index) in sortHeaderNameOpt"
                      :key="index"
                      :value="optType.value"
                    >
                      {{ optType.text }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="additional-report-grid">
              <div class="additional-type-col">
                <div class="form-group">
                  <label class="report-label">Additional Report Type</label>
                  <select
                    v-model="editedEvent.additionalReportType"
                    class="form-control input-height"
                    @change="onChangeAdditionalReportType"
                  >
                    <option
                      v-for="(optType, index) in additionalReportTypesOpt"
                      :key="index"
                      :value="optType.value"
                    >
                      {{ optType.text }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="additional-name-col">
                <div class="form-group">
                  <label class="report-label">Additional Report Name</label>
                  <input
                    v-model="editedEvent.additionalReportName"
                    type="text"
                    placeholder="Enter Additional Report Name"
                    class="form-control input-height upper"
                  />
                  <div class="input-box">
                    <p v-show="checkEditRepName" class="input-alert mb-0 mt-0">
                      Special characters are not allowed
                    </p>
                  </div>
                </div>
              </div>
              <div class="additional-file-col">
                <div class="form-group">
                  <label class="report-label">Additional File Type</label>
                  <select
                    v-model="editedEvent.additionalFileType"
                    class="form-control input-height"
                  >
                    <option
                      v-for="(optType, index) in additionalFileTypesOpt"
                      :key="index"
                      :value="optType.value"
                    >
                      {{ optType.text }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="form-buttons">
              <button class="btn btn-secondary mr-2" @click="cancelEditEvent">
                Cancel
              </button>
              <button
                class="btn btn-primary"
                @click="saveEditEvent"
                :disabled="!enableEditButton"
              >
                Update Schedule
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Edit This Day Event Form Modal -->
      <div
        v-if="showingEditThisDayForm"
        class="event-form-overlay"
        @click.self="cancelEditThisDayEvent"
      >
        <div class="event-form-container edit-form">
          <div
            class="alert alert-success alert-dismissible fade show"
            v-if="editThisDayAlert.status === 'success'"
            role="alert"
          >
            {{ editThisDayAlert.message }}
            <button
              type="button"
              class="close"
              @click="editThisDayAlert.status = ''"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div
            class="alert alert-danger alert-dismissible fade show"
            v-if="editThisDayAlert.status === 'error'"
            role="alert"
          >
            {{ editThisDayAlert.message }}
            <button
              type="button"
              class="close"
              @click="editThisDayAlert.status = ''"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <h4>
            <strong>Edit for this day</strong> - {{ formatDate(selectedDate) }}
          </h4>
          <p class="mt-4 mb-0">Report name and report type are required</p>

          <div class="form-card mt-3">
            <div class="report-type-name-grid">
              <div class="report-type-col">
                <div class="form-group">
                  <label class="report-label">Report type</label>
                  <select
                    v-model="editedThisDayEvent.reportType"
                    class="form-control input-height"
                    disabled
                  >
                    <option value="">Select an option</option>
                    <option value="ORDER_REPORT_SAME_DAY">Same day</option>
                    <option value="ORDER_REPORT_NEXT_DAY">Next day</option>
                    <option value="ORDER_REPORT_V_DAY">Specific day</option>
                    <option value="ORDER_REPORT_MAXIMUM">Maximum Report</option>
                    <option value="NOT_PULLED_REPORT">Not Pulled Report</option>
                    <option value="ERROR_REPORT">Error Report</option>
                  </select>
                </div>
              </div>
              <div class="report-name-col">
                <div class="form-group">
                  <label class="report-label">Report name</label>
                  <input
                    v-model="editedThisDayEvent.reportName"
                    type="text"
                    placeholder="ORDER_REPORT_EXAMPLE"
                    class="form-control input-height upper"
                    readonly
                  />
                  <div class="input-box">
                    <p
                      v-show="checkEditThisDayRepName"
                      class="input-alert mb-0 mt-0"
                    >
                      Special characters are not allowed
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="report-label">Delivery Window Restrict</label>
              <div class="checkbox-wrapper">
                <input
                  type="checkbox"
                  v-model="editedThisDayEvent.deliveryWindowRestrict"
                  class="checkbox-input"
                  id="editThisDayDeliveryWindowRestrict"
                />
                <label
                  class="checkbox-label"
                  for="editThisDayDeliveryWindowRestrict"
                  >Enable delivery window restriction for this day</label
                >
              </div>
            </div>

            <div class="form-group">
              <label class="report-label">
                Regions
                <span
                  >(Do not need to select for Error Report, Not Pulled
                  Report)</span
                >
              </label>
              <div class="row">
                <div
                  v-for="(region, index) in regionsOpt"
                  :key="index"
                  class="col-md-6 col-lg-4 col-6"
                >
                  <div class="checkbox-wrapper">
                    <input
                      type="checkbox"
                      :value="region.value"
                      v-model="editedThisDayEvent.deliveryRegion"
                      class="checkbox-input"
                      :id="'editThisDayRegion' + index"
                    />
                    <label
                      class="checkbox-label"
                      :for="'editThisDayRegion' + index"
                    >
                      {{ region.text }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div class="date-delivery-grid">
              <div class="date-col">
                <div class="form-group">
                  <label class="report-label">Date</label>
                  <input
                    v-model="editedThisDayEvent.date"
                    type="date"
                    class="form-control"
                    readonly
                  />
                </div>
              </div>
              <div
                class="delivery-date-col"
                v-if="editedThisDayEvent.reportType === 'ORDER_REPORT_V_DAY'"
              >
                <div class="form-group">
                  <label class="report-label">Delivery date</label>
                  <input
                    type="date"
                    v-model="editedThisDayEvent.deliveryDate"
                    class="form-control"
                  />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="report-label">Description</label>
              <input
                v-model="editedThisDayEvent.description"
                type="text"
                placeholder="Description"
                class="form-control input-height upper"
              />
            </div>

            <div class="delivery-time-tag-grid">
              <div class="delivery-time-col">
                <div class="form-group">
                  <label class="report-label"
                    >Default Delivery Start Time(12:00PM)</label
                  >
                  <input
                    v-model="editedThisDayEvent.defaultDeliveryStartTime"
                    type="text"
                    placeholder="Default Delivery Start Time(12:00PM)"
                    class="form-control input-height upper"
                  />
                </div>
              </div>
              <div class="skipped-tag-col">
                <div class="form-group">
                  <label class="report-label">Skipped Tag</label>
                  <input
                    v-model="editedThisDayEvent.skippedTag"
                    type="text"
                    placeholder="Please input tag name"
                    class="form-control input-height upper"
                  />
                </div>
              </div>
            </div>

            <div class="status-notes-grid">
              <div class="status-col">
                <div class="form-group">
                  <label class="report-label">Status for this day</label>
                  <select
                    v-model="editedThisDayEvent.dayStatus"
                    class="form-control input-height"
                  >
                    <option value="enabled">Enabled</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </div>
              </div>
              <div class="notes-col">
                <div class="form-group">
                  <label class="report-label">Notes for this day</label>
                  <textarea
                    v-model="editedThisDayEvent.dayNotes"
                    class="form-control"
                    rows="3"
                    placeholder="Add any specific notes for this day's schedule..."
                  ></textarea>
                </div>
              </div>
            </div>

            <div class="consolidate-sort-grid">
              <div class="consolidate-col">
                <div class="form-group">
                  <label class="report-label">Consolidate Order</label>
                  <select
                    v-model="editedThisDayEvent.consolidateOrder"
                    class="form-control input-height"
                  >
                    <option
                      v-for="(optType, index) in consolidateOrderOpt"
                      :key="index"
                      :value="optType.value"
                    >
                      {{ optType.text }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="sort-col">
                <div class="form-group">
                  <label class="report-label">Sort by Header Name</label>
                  <select
                    v-model="editedThisDayEvent.sortHeaderName"
                    class="form-control input-height"
                  >
                    <option
                      v-for="(optType, index) in sortHeaderNameOpt"
                      :key="index"
                      :value="optType.value"
                    >
                      {{ optType.text }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="additional-report-grid">
              <div class="additional-type-col">
                <div class="form-group">
                  <label class="report-label">Additional Report Type</label>
                  <select
                    v-model="editedThisDayEvent.additionalReportType"
                    class="form-control input-height"
                    @change="onChangeAdditionalReportTypeThisDay"
                  >
                    <option
                      v-for="(optType, index) in additionalReportTypesOpt"
                      :key="index"
                      :value="optType.value"
                    >
                      {{ optType.text }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="additional-name-col">
                <div class="form-group">
                  <label class="report-label">Additional Report Name</label>
                  <input
                    v-model="editedThisDayEvent.additionalReportName"
                    type="text"
                    placeholder="Enter Additional Report Name"
                    class="form-control input-height upper"
                  />
                  <div class="input-box">
                    <p
                      v-show="checkEditThisDayRepName"
                      class="input-alert mb-0 mt-0"
                    >
                      Special characters are not allowed
                    </p>
                  </div>
                </div>
              </div>
              <div class="additional-file-col">
                <div class="form-group">
                  <label class="report-label">Additional File Type</label>
                  <select
                    v-model="editedThisDayEvent.additionalFileType"
                    class="form-control input-height"
                  >
                    <option
                      v-for="(optType, index) in additionalFileTypesOpt"
                      :key="index"
                      :value="optType.value"
                    >
                      {{ optType.text }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="form-buttons">
              <button
                class="btn btn-secondary mr-2"
                @click="cancelEditThisDayEvent"
              >
                Cancel
              </button>
              <button
                class="btn btn-primary"
                @click="saveEditThisDayEvent"
                :disabled="!enableEditThisDayButton"
              >
                Update This Day
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import NewScheduleForm from "@/components/forms/NewScheduleForm.vue";

export default {
  name: "PullSchedules",
  components: {
    NewScheduleForm
  },
  data() {
    const today = dayjs().format("YYYY-MM-DD");
    return {
      selectedDate: today,
      selectedReportType: "",
      openMenuId: null,
      currentEvent: null,
      menuPosition: { top: 0, left: 0 },
      loading: false,
      events: [],
      schedules: [],
      reports: [],
      reportsMap: {},
      showingNewEventForm: false,
      showingEditEventForm: false,
      showingEditThisDayForm: false,
      alert: {
        status: "",
        message: ""
      },
      editAlert: {
        status: "",
        message: ""
      },
      editThisDayAlert: {
        status: "",
        message: ""
      },

      editedEvent: {
        reportName: "",
        reportType: "",
        deliveryRegion: [],
        deliveryDate: "",
        description: "",
        defaultDeliveryStartTime: "",
        skippedTag: "",
        deliveryWindowRestrict: false,
        consolidateOrder: false,
        sortHeaderName: "Bouquet",
        additionalReportType: "",
        additionalReportName: "",
        additionalFileType: ""
      },
      editedThisDayEvent: {
        reportName: "",
        reportType: "",
        deliveryRegion: [],
        deliveryDate: "",
        description: "",
        defaultDeliveryStartTime: "",
        skippedTag: "",
        deliveryWindowRestrict: false,
        date: "",
        startTime: "",
        endTime: "",
        dayStatus: "enabled",
        dayNotes: "",
        consolidateOrder: false,
        sortHeaderName: "Bouquet",
        additionalReportType: "",
        additionalReportName: "",
        additionalFileType: ""
      },
      regionsOpt: [],
      additionalReportTypesOpt: [
        { text: "Select an option", value: "" },
        { text: "Gopher Report", value: "ADDITIONAL_GOPHER_IT_REPORT" },
        { text: "Flashbox Report", value: "ADDITIONAL_FLASHBOX_REPORT" },
        { text: "Midland Report", value: "ADDITIONAL_MIDLAND_REPORT" },
        {
          text: "Go Speed Logistics Report",
          value: "ADDITIONAL_GO_SPEED_LOGISTICS_REPORT"
        }
      ],
      additionalFileTypesOpt: [
        { text: "Select an option", value: "" },
        { text: "Excel", value: "excel" },
        { text: "CSV", value: "csv" }
      ],
      consolidateOrderOpt: [
        { text: "False", value: false },
        { text: "True", value: true }
      ],
      sortHeaderNameOpt: [
        { text: "Bouquet", value: "Bouquet" },
        { text: "Recipient_Name", value: "Recipient_Name" }
      ]
    };
  },
  computed: {
    timeSlots() {
      const slots = [];
      for (let hour = 4; hour <= 23; hour++) {
        slots.push({
          time: hour,
          display: `${hour.toString().padStart(2, "0")}:00`
        });
      }
      return slots;
    },
    eventsForSelectedDate() {
      return this.events.filter(event => {
        // Extract date part from event start time and compare directly
        const eventDate = event.start.slice(0, 10);
        const dateMatches = eventDate === this.selectedDate;

        // Filter by report type if selected
        let typeMatches = false;

        if (!this.selectedReportType) {
          // Show all types when no filter is selected
          typeMatches = true;
        } else if (this.selectedReportType === "ERROR_CHECK_REPORT") {
          // Show NOT_PULLED_REPORT and NOT_SENT_REPORT
          typeMatches =
            event.reportType === "NOT_PULLED_REPORT" ||
            event.reportType === "NOT_SENT_REPORT" ||
            event.reportType === "ERROR_REPORT";
        } else if (this.selectedReportType === "REGULAR_REPORT") {
          // Show all reports except NOT_PULLED_REPORT and NOT_SENT_REPORT
          typeMatches =
            event.reportType !== "NOT_PULLED_REPORT" &&
            event.reportType !== "NOT_SENT_REPORT" &&
            event.reportType !== "ERROR_REPORT";
        } else {
          // Show exact match for specific report type
          typeMatches = event.reportType === this.selectedReportType;
        }

        return dateMatches && typeMatches;
      });
    },
    showCurrentTimeLine() {
      // Only show current time line if viewing today's date
      const today = dayjs().format("YYYY-MM-DD");
      return this.selectedDate === today;
    },
    currentTimePosition() {
      // For demo purposes, let's set current time to 6:00 PM (18:00)
      const currentHour = 18; // 6 PM
      const currentMinutes = 0;

      // Calculate position based on time (starting from 4 AM)
      const currentTime = currentHour + currentMinutes / 60;
      return (currentTime - 4) * 120;
    },
    checkEditRepName() {
      if (
        this.editedEvent.reportName &&
        !/^[0-9A-Za-z_ ]+$/.test(this.editedEvent.reportName)
      ) {
        return true;
      }
      return false;
    },
    enableEditButton() {
      if (
        (this.editedEvent.reportType === "ERROR_REPORT" ||
          this.editedEvent.reportType === "NOT_PULLED_REPORT" ||
          this.editedEvent.deliveryRegion.length > 0) &&
        this.editedEvent.reportType &&
        this.editedEvent.reportName &&
        /^[0-9A-Za-z_ ]+$/.test(this.editedEvent.reportName)
      ) {
        return true;
      } else {
        return false;
      }
    },
    checkEditThisDayRepName() {
      if (
        this.editedThisDayEvent.reportName &&
        !/^[0-9A-Za-z_ ]+$/.test(this.editedThisDayEvent.reportName)
      ) {
        return true;
      }
      return false;
    },
    enableEditThisDayButton() {
      if (
        (this.editedThisDayEvent.reportType === "ERROR_REPORT" ||
          this.editedThisDayEvent.reportType === "NOT_PULLED_REPORT" ||
          this.editedThisDayEvent.deliveryRegion.length > 0) &&
        this.editedThisDayEvent.reportType &&
        this.editedThisDayEvent.reportName &&
        /^[0-9A-Za-z_ ]+$/.test(this.editedThisDayEvent.reportName)
      ) {
        return true;
      } else {
        return false;
      }
    }
  },
  async created() {
    this.fetchEvents();

    // Fetch regions data like in OrderReports.vue
    try {
      const response = await this.axios.get(
        process.env.VUE_APP_API_ADMIN_API_ENDPOINT + "/order-pull/regions",
        {
          headers: {
            Authorization: "Bearer " + this.$store.getters.idToken
          }
        }
      );

      if (response.data && response.data.records) {
        this.regionsOpt = [];
        for (const data of response.data.records) {
          const region = {
            text: data.name + "(" + data.province_code + ")",
            value: data.tag,
            provinceCode: data.province_code
          };
          this.regionsOpt.push(region);
        }
        this.regionsOpt.sort((a, b) => (a.text > b.text ? 1 : -1));
      }
    } catch (error) {
      console.error("Error fetching regions:", error);
      // Keep the default regions if API fails
    }
  },
  mounted() {
    document.addEventListener("click", this.resetMenu);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.resetMenu);
  },
  methods: {
    async fetchEvents() {
      try {
        this.loading = true;

        // Fetch schedules and reports data
        const [
          schedulesResponse,
          reportsResponse,
          overrideResponse
        ] = await Promise.all([
          this.axios.get(
            `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/schedules`
          ),
          this.axios.get(
            `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/config`
          ),
          this.axios.get(
            `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/override`
          )
        ]);

        this.schedules = schedulesResponse?.data || [];
        this.reports = reportsResponse?.data || [];
        this.closed = overrideResponse?.data?.closed || [];
        this.override = overrideResponse?.data?.override || [];

        this.reports.forEach(report => {
          this.reportsMap[report.id] = report;
        });

        // Generate events for the selected date
        this.generateEventsForSelectedDate();
      } catch (error) {
        console.error("Error fetching events:", error);
        // Fallback to empty events array
        this.events = [];
      } finally {
        this.loading = false;
      }
    },

    async refreshData() {
      await this.fetchEvents();
      this.displayMessage("success", "Data refreshed successfully");
    },

    convertScheduleToEvents(schedule, report) {
      console.log("schedule:", schedule);
      console.log("report:", report);
      console.log("scheduleExpression:", schedule.scheduleExpression);
      const events = [];

      // Parse cron expression (AWS EventBridge format)
      // Format: "cron(minutes hours day-of-month month day-of-week year)"
      let cronExpression = schedule.scheduleExpression;
      if (cronExpression.startsWith("cron(") && cronExpression.endsWith(")")) {
        cronExpression = cronExpression.slice(5, -1); // Remove "cron(" and ")"
      }

      const cronParts = cronExpression.split(" ");
      if (cronParts.length !== 6) {
        console.warn("Invalid cron expression:", schedule.scheduleExpression);
        return events;
      }

      const [minutes, hours, dayOfMonth, month, dayOfWeek, year] = cronParts;
      console.log("dayOfWeek:", dayOfWeek);

      // Parse the selected date using dayjs
      const selectedDate = dayjs(this.selectedDate);
      const selectedDateForClosed = selectedDate.format("MM/DD/YYYY");
      console.log("this.selectedDate:", this.selectedDate);
      console.log("selected date:", selectedDate.format());
      const selectedYear = selectedDate.year();
      const selectedMonth = selectedDate.month() + 1; // dayjs months are 0-based, convert to 1-based
      const selectedDay = selectedDate.date();
      const selectedDayOfWeek = selectedDate.day(); // 0 = Sunday, 1 = Monday, etc.
      console.log("selectedYear:", selectedYear);
      console.log("selectedMonth:", selectedMonth);
      console.log("selectedDay:", selectedDay);
      console.log("selectedDayOfWeek:", selectedDayOfWeek);

      // Check if schedule matches the selected date
      if (
        !this.cronMatches(year, selectedYear) ||
        !this.cronMatches(month, selectedMonth) ||
        !this.cronMatches(dayOfMonth, selectedDay) ||
        !this.cronMatchesDayOfWeek(dayOfWeek, selectedDayOfWeek)
      ) {
        console.log("not matched");
        return events;
      }

      // Parse time values - handle multiple values, ranges, and wildcards
      const hourValues = this.parseCronField(hours, 0, 23);
      const minuteValues = this.parseCronField(minutes, 0, 59);
      console.log("hourValues:", hourValues);
      console.log("minuteValues:", minuteValues);

      const closedItem = this.closed.find(item => item.id === report.id);
      let closedThisDay = false;
      if (closedItem?.closed_dates) {
        closedThisDay = closedItem.closed_dates.find(
          item => item === selectedDateForClosed
        )
          ? true
          : false;
      }

      const overrideItem = this.override.find(item => item.id === report.id);
      let overrideThisDay = false;
      if (overrideItem?.closed_dates) {
        overrideThisDay = overrideItem[selectedDateForClosed] ? true : false;
      }

      // Generate events for each time combination
      hourValues.forEach(eventHour => {
        minuteValues.forEach(eventMinute => {
          const startTime = `${eventHour
            .toString()
            .padStart(2, "0")}:${eventMinute.toString().padStart(2, "0")}`;

          // Calculate end time (30-minute duration)
          let finalEndHour = eventHour;
          let finalEndMinute = eventMinute + 30;
          if (finalEndMinute >= 60) {
            finalEndHour += 1;
            finalEndMinute -= 60;
          }

          const endTime = `${finalEndHour
            .toString()
            .padStart(2, "0")}:${finalEndMinute.toString().padStart(2, "0")}`;

          events.push({
            id: `${schedule.name}_${report.id}_${eventHour}_${eventMinute}`,
            reportName: report.reportName || schedule.name,
            reportType: report.reportType || "ORDER_REPORT_SAME_DAY",
            deliveryRegion: report.deliveryRegion || "",
            start: `${this.selectedDate} ${startTime}`,
            end: `${this.selectedDate} ${endTime}`,
            scheduleId: schedule.name,
            reportId: report.id,
            status: schedule.state === "ENABLED" ? "active" : "disabled",
            description: schedule.description || "",
            isDisabledThisDay: closedThisDay,
            isOverride: overrideThisDay
          });
        });
      });

      console.log("evnets:", events);
      return events;
    },

    // Helper method to check if a cron field matches a value
    cronMatches(cronField, value) {
      if (cronField === "*") return true;
      if (cronField === "?") return true;

      // Handle single values
      if (!isNaN(cronField)) {
        return parseInt(cronField) === value;
      }

      // Handle comma-separated values (e.g., "1,3,5")
      if (cronField.includes(",")) {
        const values = cronField.split(",").map(v => parseInt(v.trim()));
        return values.includes(value);
      }

      // Handle ranges (e.g., "1-5")
      if (cronField.includes("-")) {
        const [start, end] = cronField.split("-").map(v => parseInt(v.trim()));
        return value >= start && value <= end;
      }

      // Handle step values (e.g., "*/2", "1-10/2")
      if (cronField.includes("/")) {
        const [range, step] = cronField.split("/");
        const stepValue = parseInt(step);

        if (range === "*") {
          return value % stepValue === 0;
        } else if (range.includes("-")) {
          const [start, end] = range.split("-").map(v => parseInt(v.trim()));
          return (
            value >= start && value <= end && (value - start) % stepValue === 0
          );
        }
      }

      return false;
    },

    // Helper method to check day of week matching (handles AWS EventBridge format)
    cronMatchesDayOfWeek(cronField, jsDay) {
      if (cronField === "*" || cronField === "?") return true;

      // Convert day names to numbers
      const dayNameMap = {
        SUN: 0,
        MON: 1,
        TUE: 2,
        WED: 3,
        THU: 4,
        FRI: 5,
        SAT: 6,
        Sun: 0,
        Mon: 1,
        Tue: 2,
        Wed: 3,
        Thu: 4,
        Fri: 5,
        Sat: 6
      };

      // Handle day name ranges like "Sun-Sat"
      if (
        cronField.includes("-") &&
        (isNaN(cronField.split("-")[0]) || isNaN(cronField.split("-")[1]))
      ) {
        const [startDay, endDay] = cronField.split("-").map(d => d.trim());
        console.log("startDay:", startDay, ", endDay:", endDay);

        // Convert day names to numbers
        let startNum = dayNameMap[startDay.toUpperCase()];
        let endNum = dayNameMap[endDay.toUpperCase()];
        console.log("startNum:", startNum, ", endNum:", endNum);

        // If we couldn't convert, try as numbers
        if (startNum === undefined) startNum = parseInt(startDay);
        if (endNum === undefined) endNum = parseInt(endDay);

        // Handle AWS EventBridge format where 1=Sunday
        if (startNum === undefined && !isNaN(startDay)) {
          startNum = parseInt(startDay);
          if (startNum === 1) startNum = 0;
          // Convert 1=Sunday to 0=Sunday
          else if (startNum >= 2 && startNum <= 7) startNum = startNum - 1; // Convert 2-7 to 1-6
        }

        if (endNum === undefined && !isNaN(endDay)) {
          endNum = parseInt(endDay);
          if (endNum === 1) endNum = 0;
          // Convert 1=Sunday to 0=Sunday
          else if (endNum >= 2 && endNum <= 7) endNum = endNum - 1; // Convert 2-7 to 1-6
        }

        // Special case: Sun-Sat means every day
        if (
          (startNum === 0 && endNum === 6) ||
          (startDay.toUpperCase() === "SUN" && endDay.toUpperCase() === "SAT")
        ) {
          return true;
        }

        // Handle wrap-around ranges (e.g., Fri-Mon)
        if (startNum > endNum) {
          return jsDay >= startNum || jsDay <= endNum;
        } else {
          return jsDay >= startNum && jsDay <= endNum;
        }
      }

      // Handle single day names
      if (dayNameMap[cronField.toUpperCase()] !== undefined) {
        return jsDay === dayNameMap[cronField.toUpperCase()];
      }

      // Convert JS day (0=Sunday) to cron day format
      // AWS EventBridge: 1=Sunday, 2=Monday, ..., 7=Saturday
      // But also supports: 0=Sunday, 1=Monday, ..., 6=Saturday
      const awsDay = jsDay === 0 ? 1 : jsDay + 1; // Convert to 1=Sunday format
      console.log("jsDay:", jsDay);
      console.log("awsDay:", awsDay);
      const standardDay = jsDay; // Keep 0=Sunday format

      // Handle single numeric values
      if (!isNaN(cronField)) {
        const cronDay = parseInt(cronField);
        return (
          cronDay === awsDay ||
          cronDay === standardDay ||
          (cronDay === 7 && jsDay === 0) ||
          (cronDay === 0 && jsDay === 0)
        );
      }

      // Handle comma-separated values (could be numbers or day names)
      if (cronField.includes(",")) {
        const values = cronField.split(",").map(v => v.trim());
        return values.some(value => {
          // Check if it's a day name
          if (dayNameMap[value.toUpperCase()] !== undefined) {
            return jsDay === dayNameMap[value.toUpperCase()];
          }
          // Check if it's a number
          if (!isNaN(value)) {
            const cronDay = parseInt(value);
            return (
              cronDay === awsDay ||
              cronDay === standardDay ||
              (cronDay === 7 && jsDay === 0) ||
              (cronDay === 0 && jsDay === 0)
            );
          }
          return false;
        });
      }

      // Handle numeric ranges
      if (
        cronField.includes("-") &&
        !isNaN(cronField.split("-")[0]) &&
        !isNaN(cronField.split("-")[1])
      ) {
        const [start, end] = cronField.split("-").map(v => parseInt(v.trim()));
        console.log("start:", start, ", end:", end);
        return (
          (awsDay >= start && awsDay <= end) ||
          (standardDay >= start && standardDay <= end)
        );
      }

      return false;
    },

    // Helper method to parse cron field and return array of values
    parseCronField(cronField, min, max) {
      if (cronField === "*") {
        // For time fields, return reasonable defaults instead of all values
        if (max === 23) return [12]; // Default hour: noon
        if (max === 59) return [0]; // Default minute: :00
        return Array.from({ length: max - min + 1 }, (_, i) => min + i);
      }

      if (!isNaN(cronField)) {
        return [parseInt(cronField)];
      }

      if (cronField.includes(",")) {
        return cronField
          .split(",")
          .map(v => parseInt(v.trim()))
          .filter(v => v >= min && v <= max);
      }

      if (cronField.includes("-")) {
        const [start, end] = cronField.split("-").map(v => parseInt(v.trim()));
        return Array.from(
          { length: end - start + 1 },
          (_, i) => start + i
        ).filter(v => v >= min && v <= max);
      }

      if (cronField.includes("/")) {
        const [range, step] = cronField.split("/");
        const stepValue = parseInt(step);
        const values = [];

        if (range === "*") {
          for (let i = min; i <= max; i += stepValue) {
            values.push(i);
          }
        } else if (range.includes("-")) {
          const [start, end] = range.split("-").map(v => parseInt(v.trim()));
          for (let i = start; i <= end; i += stepValue) {
            values.push(i);
          }
        }

        return values.filter(v => v >= min && v <= max);
      }

      return [min]; // Fallback
    },

    generateEventsForSelectedDate() {
      // Convert schedules to calendar events for the selected date
      this.events = [];
      console.log("schedules:", this.schedules);
      this.schedules.forEach(schedule => {
        if (
          schedule.input &&
          schedule.input.uuid &&
          this.reportsMap[schedule.input.uuid]
        ) {
          const report = this.reportsMap[schedule.input.uuid];
          const events = this.convertScheduleToEvents(schedule, report);
          console.log("converted events", this.events);
          this.events.push(...events);
        } else {
          console.warn("not in report config: ", schedule);
        }
      });
      console.log("total events", this.events);
    },

    formatDate(dateString) {
      // Use dayjs to format the date
      return dayjs(dateString).format("dddd, MMMM D, YYYY");
    },

    formatEventTime(event) {
      const start = dayjs(event.start);
      const end = dayjs(event.end);
      return `${start.format("HH:mm")} - ${end.format("HH:mm")}`;
    },

    formatReportType(reportType) {
      const typeMap = {
        ORDER_REPORT_SAME_DAY: "Same Day",
        ORDER_REPORT_NEXT_DAY: "Next Day",
        ORDER_REPORT_V_DAY: "V Day",
        ORDER_REPORT_MAXIMUM: "Maximum",
        NOT_PULLED_REPORT: "Not Pulled",
        ERROR_REPORT: "Error Report"
      };
      return typeMap[reportType] || reportType;
    },

    getEventStatus(event) {
      // Check status in priority order
      if (event.isDisabledThisDay) {
        return "Disabled This Day";
      }
      if (event.isOverride) {
        return "Overrided";
      }
      if (event.status === "disabled") {
        return "Disabled";
      }
      return null; // No status to display
    },

    getEventStyle(event) {
      const start = dayjs(event.start);
      const end = dayjs(event.end);
      console.log(start.format(), end.format());

      // Calculate position based on time
      const startHour = start.hour() + start.minute() / 60;
      const endHour = end.hour() + end.minute() / 60;
      const duration = endHour - startHour;

      // Position from 4 AM (hour 4)
      const topPosition = (startHour - 4) * 120;
      const height = duration * 120;

      // Handle overlapping events
      const overlappingEvents = this.getOverlappingEvents(event);
      const eventIndex = overlappingEvents.indexOf(event.id);
      const totalOverlapping = overlappingEvents.length;

      let width = 300;
      let left = 0;

      if (totalOverlapping > 1) {
        width = Math.max(250, 300 / totalOverlapping);
        left = eventIndex * (width + 10);
      }

      return {
        position: "absolute",
        top: `${topPosition}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${Math.max(height, 40)}px`,
        zIndex: 10
      };
    },

    getOverlappingEvents(targetEvent) {
      const targetStart = dayjs(targetEvent.start);
      const targetEnd = dayjs(targetEvent.end);

      return this.eventsForSelectedDate
        .filter(event => {
          const eventStart = dayjs(event.start);
          const eventEnd = dayjs(event.end);

          return (
            eventStart.isBefore(targetEnd) && eventEnd.isAfter(targetStart)
          );
        })
        .map(event => event.id)
        .sort();
    },

    previousDay() {
      this.selectedDate = dayjs(this.selectedDate)
        .subtract(1, "day")
        .format("YYYY-MM-DD");
      this.generateEventsForSelectedDate();
    },

    nextDay() {
      this.selectedDate = dayjs(this.selectedDate)
        .add(1, "day")
        .format("YYYY-MM-DD");
      this.generateEventsForSelectedDate();
    },

    onDateChange() {
      // Regenerate events when date changes
      this.generateEventsForSelectedDate();
    },

    onReportTypeChange() {
      // Report type filter automatically updates eventsForSelectedDate
      // This method can be used for additional logic if needed
    },

    selectEvent(event) {
      // Handle event selection if needed
    },

    getScheduleState(scheduleId) {
      const schedule = this.schedules.find(s => s.name === scheduleId);
      return schedule ? schedule.state : "DISABLED";
    },

    showMenu(eventDomEvent, calendarEvent) {
      const rect = eventDomEvent.currentTarget.getBoundingClientRect();
      this.menuPosition = {
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      };
      this.openMenuId = calendarEvent.id;
      this.currentEvent = calendarEvent;
    },

    resetMenu() {
      this.openMenuId = null;
      this.currentEvent = null;
    },

    displayMessage(status, message) {
      this.alert.status = status;
      this.alert.message = message;
    },

    editEvent(event) {
      // Populate edit form with current event data
      const report = this.reportsMap[event.reportId];
      if (report) {
        this.editedEvent = {
          reportName: report.reportName || event.reportName,
          reportType: report.reportType || event.reportType,
          deliveryRegion: report.deliveryRegion || [],
          deliveryDate: report.deliveryDate || "",
          description: report.description || "",
          defaultDeliveryStartTime: report.defaultDeliveryStartTime || "",
          skippedTag: report.skippedTag || "",
          deliveryWindowRestrict: report.deliveryWindowExcluded || false,
          consolidateOrder: report.consolidateOrder || false,
          sortHeaderName: report.sortHeaderName || "Bouquet",
          additionalReportType: report.additionalReportType || "",
          additionalReportName: report.additionalReportName || "",
          additionalFileType: report.additionalFileType || "",
          scheduleId: event.scheduleId,
          reportId: event.reportId
        };
      }

      this.showingEditEventForm = true;
      this.resetMenu();
    },

    editThisDayEvent(event) {
      // Populate edit this day form with current event data
      const eventDate = dayjs(event.start).format("YYYY-MM-DD");
      const startTime = dayjs(event.start).format("HH:mm");
      const endTime = dayjs(event.end).format("HH:mm");

      const report = this.reportsMap[event.reportId];

      this.editedThisDayEvent = {
        reportName: event.reportName,
        reportType: report?.reportType || event.reportType,
        deliveryRegion: report?.deliveryRegion || [],
        deliveryDate: report?.deliveryDate || "",
        description: report?.description || "",
        defaultDeliveryStartTime: report?.defaultDeliveryStartTime || "",
        skippedTag: report?.skippedTag || "",
        deliveryWindowRestrict: report?.deliveryWindowExcluded || false,
        consolidateOrder: report?.consolidateOrder || false,
        sortHeaderName: report?.sortHeaderName || "Bouquet",
        additionalReportType: report?.additionalReportType || "",
        additionalReportName: report?.additionalReportName || "",
        additionalFileType: report?.additionalFileType || "",
        date: eventDate,
        startTime: startTime,
        endTime: endTime,
        dayStatus: event.isDisabledThisDay ? "disabled" : "enabled",
        dayNotes: "",
        scheduleId: event.scheduleId,
        reportId: event.reportId,
        eventId: event.id
      };

      this.showingEditThisDayForm = true;
      this.resetMenu();
    },

    async manuallyGenReport(event) {
      if (confirm(`Manually generate report for ${event.reportName}?`)) {
        this.loading = true;

        // Make API call to manually trigger the report generation
        const params = {
          uuid: event.reportId,
          prefix: "(Manual)"
        };
        console.log("event:", event);
        this.$axios
          .post(
            process.env.VUE_APP_API_ADMIN_API_ENDPOINT + `/order-pull/export`,
            params
          )
          .then(() => {
            this.displayMessage(
              "success",
              `Report ${event.reportName} has been manually triggered for generation.`
            );
          })
          .catch(() => {
            this.displayMessage("error", "Unable to generate report");
          })
          .finally(() => {
            this.loading = false;
            this.reportGenJob = null;
          });
      }
      this.resetMenu();
    },

    async disableThisDayEvent(event) {
      const eventDate = dayjs(event.start).format("YYYY-MM-DD");
      const eventTime = dayjs(event.start).format("HH:mm");
      // event.status = "disabled";

      if (
        confirm(
          `Disable this day's event for ${event.reportName} on ${eventDate} at ${eventTime}?`
        )
      ) {
        this.loading = true;
        const url = `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/override/disable/${event.reportId}`;
        console.log("url:", url);
        const params = {
          date: eventDate
        };
        console.log("params:", params);
        this.$axios
          .post(url, params)
          .then(() => {
            this.displayMessage("success", "Successfully change status");
          })
          .catch(() => {
            this.displayMessage("error", "Unable to change status");
          })
          .finally(() => {
            this.loading = false;
          });

        this.displayMessage(
          "success",
          `Event for ${event.reportName} on ${eventDate} has been disabled.`
        );
      }
      this.resetMenu();
    },

    async enableThisDayEvent(event) {
      const eventDate = dayjs(event.start).format("YYYY-MM-DD");
      const eventTime = dayjs(event.start).format("HH:mm");

      if (
        confirm(
          `Enable this day's event for ${event.reportName} on ${eventDate} at ${eventTime}?`
        )
      ) {
        this.loading = true;
        const url = `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/override/enable/${event.reportId}`;
        console.log("url:", url);
        const params = {
          date: eventDate
        };
        console.log("params:", params);
        this.$axios
          .post(url, params)
          .then(() => {
            this.displayMessage("success", "Successfully change status");
          })
          .catch(() => {
            this.displayMessage("error", "Unable to change status");
          })
          .finally(() => {
            this.loading = false;
          });

        this.displayMessage(
          "success",
          `Event for ${event.reportName} on ${eventDate} has been disabled.`
        );
      }
      this.resetMenu();
    },

    async enableEvent(event) {
      if (confirm("Are you sure you want to enable this schedule?")) {
        try {
          this.loading = true;
          const params = {
            type: "enable",
            name: event.scheduleId
          };

          await this.axios.put(
            process.env.VUE_APP_API_ADMIN_API_ENDPOINT +
              "/order-pull/schedules",
            params
          );

          // Update local state
          const schedule = this.schedules.find(
            s => s.name === event.scheduleId
          );
          if (schedule) {
            schedule.state = "ENABLED";
          }

          // Regenerate events to reflect the change
          this.generateEventsForSelectedDate();
        } catch (error) {
          console.error("Error enabling schedule:", error);
          this.displayMessage(
            "error",
            "Failed to enable schedule. Please try again."
          );
        } finally {
          this.loading = false;
        }
      }
      this.resetMenu();
    },

    async disableEvent(event) {
      if (confirm("Are you sure you want to disable this schedule?")) {
        try {
          this.loading = true;
          const params = {
            type: "disable",
            name: event.scheduleId
          };

          await this.axios.put(
            process.env.VUE_APP_API_ADMIN_API_ENDPOINT +
              "/order-pull/schedules",
            params
          );

          // Update local state
          const schedule = this.schedules.find(
            s => s.name === event.scheduleId
          );
          if (schedule) {
            schedule.state = "DISABLED";
          }

          // Regenerate events to reflect the change
          this.generateEventsForSelectedDate();
        } catch (error) {
          console.error("Error disabling schedule:", error);
          this.displayMessage(
            "error",
            "Failed to disable schedule. Please try again."
          );
        } finally {
          this.loading = false;
        }
      }
      this.resetMenu();
    },

    deleteEvent(event) {
      this.events = this.events.filter(e => e.id !== event.id);
      this.resetMenu();
    },

    onChangeAdditionalReportType() {
      if (
        this.editedEvent.additionalReportType === "ADDITIONAL_GOPHER_IT_REPORT"
      ) {
        this.editedEvent.additionalFileType = "csv";
      } else {
        this.editedEvent.additionalFileType = "";
        this.editedEvent.additionalReportName = "";
      }
    },

    onChangeAdditionalReportTypeThisDay() {
      if (
        this.editedThisDayEvent.additionalReportType ===
        "ADDITIONAL_GOPHER_IT_REPORT"
      ) {
        this.editedThisDayEvent.additionalFileType = "csv";
      } else {
        this.editedThisDayEvent.additionalFileType = "";
        this.editedThisDayEvent.additionalReportName = "";
      }
    },

    showNewEventForm() {
      this.showingNewEventForm = true;
    },

    cancelNewEvent() {
      this.showingNewEventForm = false;
    },

    saveNewEvent(formData) {
      const newId = dayjs()
        .valueOf()
        .toString();
      const start = `${formData.date} ${formData.startTime}`;
      const end = `${formData.date} ${formData.endTime}`;

      this.events.push({
        id: newId,
        start,
        end,
        reportName: formData.reportName,
        reportType: formData.reportType,
        status: "active",
        deliveryRegion: ""
      });

      this.showingNewEventForm = false;
    },
    async saveEditEvent() {
      try {
        // Prepare the form data similar to OrderReports.vue
        const formData = {
          uuid: this.editedEvent.reportId,
          reportName: this.editedEvent.reportName
            .trim()
            .replace(/\s/g, "_")
            .toUpperCase(),
          description: this.editedEvent.description,
          reportType: this.editedEvent.reportType,
          errorCheckReport: this.editedEvent.reportType === "ERROR_REPORT",
          fileType: "excel",
          deliveryDateFilterIncluded:
            this.editedEvent.reportType !== "ORDER_REPORT_V_DAY" &&
            this.editedEvent.reportType !== "ERROR_REPORT",
          noOfDaysToDeliveryDate: this.getNoOfDaysToDeliveryDate(),
          deliveryDate: this.editedEvent.deliveryDate,
          filtersForRestUrl: {
            fulfillmentStatus: "unshipped",
            fields:
              "id,name,note,discount_codes,line_items,shipping_address,shipping_lines,tags,created_at",
            status: "open"
          },
          deliveryRegion: this.editedEvent.deliveryRegion,
          excludingRegion: [],
          provinceCode: this.getProvinceCode(),
          deliveryWindowExcluded: this.editedEvent.deliveryWindowRestrict,
          deliveryWindow: [
            "(1pm - 6pm)",
            "(1pm - 5pm)",
            "(3pm - 6pm)",
            "(3pm - 7pm)"
          ],
          createAtIncluded: false,
          hours: 0,
          minutes: 0,
          defaultDeliveryStartTime: this.editedEvent.defaultDeliveryStartTime,
          skippedTag: this.editedEvent.skippedTag,
          sortHeaderName: this.getSortHeaderName(),
          consolidateOrder: this.getConsolidateOrder()
        };

        // Make API call to update the report configuration
        await this.axios.put(
          `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/config/${this.editedEvent.reportId}`,
          formData,
          {
            headers: {
              Authorization: "Bearer " + this.$store.getters.idToken
            }
          }
        );

        this.editAlert = {
          status: "success",
          message: "Schedule updated successfully"
        };

        // Refresh the events after a short delay
        setTimeout(() => {
          this.fetchEvents();
          this.showingEditEventForm = false;
          this.editAlert = { status: "", message: "" };
        }, 2000);
      } catch (error) {
        console.error("Error updating schedule:", error);
        this.editAlert = {
          status: "error",
          message: "Failed to update schedule. Please try again."
        };
      }
    },

    async saveEditThisDayEvent() {
      try {
        // Create override for this specific day
        const overrideData = {
          reportId: this.editedThisDayEvent.reportId,
          date: this.editedThisDayEvent.date,
          startTime: this.editedThisDayEvent.startTime,
          endTime: this.editedThisDayEvent.endTime,
          status: this.editedThisDayEvent.dayStatus,
          notes: this.editedThisDayEvent.dayNotes
        };

        // Make API call to create/update day-specific override
        await this.axios.post(
          `${process.env.VUE_APP_API_ADMIN_API_ENDPOINT}/order-pull/override/${this.editedThisDayEvent.reportId}`,
          overrideData,
          {
            headers: {
              Authorization: "Bearer " + this.$store.getters.idToken
            }
          }
        );

        this.editThisDayAlert = {
          status: "success",
          message: "Day-specific schedule updated successfully"
        };

        // Refresh the events after a short delay
        setTimeout(() => {
          this.fetchEvents();
          this.showingEditThisDayForm = false;
          this.editThisDayAlert = { status: "", message: "" };
        }, 2000);
      } catch (error) {
        console.error("Error updating day-specific schedule:", error);
        this.editThisDayAlert = {
          status: "error",
          message: "Failed to update day-specific schedule. Please try again."
        };
      }
    },

    cancelEditEvent() {
      this.showingEditEventForm = false;
      this.editAlert = { status: "", message: "" };
      this.editedEvent = {
        reportName: "",
        reportType: "",
        deliveryRegion: [],
        deliveryDate: "",
        description: "",
        defaultDeliveryStartTime: "",
        skippedTag: "",
        deliveryWindowRestrict: false
      };
    },

    cancelEditThisDayEvent() {
      this.showingEditThisDayForm = false;
      this.editThisDayAlert = { status: "", message: "" };
      this.editedThisDayEvent = {
        reportName: "",
        reportType: "",
        deliveryRegion: [],
        deliveryDate: "",
        description: "",
        defaultDeliveryStartTime: "",
        skippedTag: "",
        deliveryWindowRestrict: false,
        date: "",
        startTime: "",
        endTime: "",
        dayStatus: "enabled",
        dayNotes: ""
      };
    },

    getNoOfDaysToDeliveryDate() {
      if (this.editedEvent.reportType === "ORDER_REPORT_SAME_DAY") {
        return 0;
      } else if (
        this.editedEvent.reportType === "ORDER_REPORT_NEXT_DAY" ||
        this.editedEvent.reportType === "ORDER_REPORT_MAXIMUM"
      ) {
        return 1;
      }
      return 0;
    },

    getProvinceCode() {
      if (this.editedEvent.deliveryRegion.length > 0) {
        const region = this.editedEvent.deliveryRegion[0];
        const regionOpt = this.regionsOpt.find(t => {
          return t.text.toLowerCase() === region.toLowerCase();
        });
        return regionOpt ? regionOpt.provinceCode : "";
      }
      return "";
    },

    getSortHeaderName() {
      if (
        this.editedEvent.reportType === "ORDER_REPORT_V_DAY" ||
        this.editedEvent.reportType === "ORDER_REPORT_SAME_DAY" ||
        this.editedEvent.reportType === "ORDER_REPORT_NEXT_DAY"
      ) {
        return "Recipient_Name";
      }
      return "";
    },

    getConsolidateOrder() {
      if (
        this.editedEvent.reportType === "ORDER_REPORT_V_DAY" ||
        this.editedEvent.reportType === "ORDER_REPORT_SAME_DAY" ||
        this.editedEvent.reportType === "ORDER_REPORT_NEXT_DAY"
      ) {
        return true;
      }
      return false;
    }
  }
};
</script>

<style scoped>
.calendar-container {
  position: relative;
  font-family: Arial, sans-serif;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #ddd;
}

.date-navigation {
  display: flex;
  align-items: center;
  gap: 15px;
}

.date-picker-container {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
}

.date-picker {
  width: 150px;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.date-picker:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.date-display {
  color: #495057;
  font-weight: 600;
  white-space: nowrap;
}

.header-filters {
  display: flex;
  align-items: center;
  gap: 15px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  margin: 0;
  white-space: nowrap;
}

.report-type-selector {
  width: 180px;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
}

.report-type-selector:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.calendar-body {
  display: flex;
  height: 600px;
  overflow-y: auto;
}

.time-column {
  width: 80px;
  border-right: 1px solid #ddd;
  background-color: #f8f9fa;
}

.time-header {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border-bottom: 1px solid #ddd;
  background-color: #e9ecef;
}

.time-slot {
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.time-label {
  font-size: 12px;
  color: #666;
}

.events-column {
  flex: 1;
  position: relative;
}

.events-header {
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  border-bottom: 1px solid #ddd;
  background-color: #e9ecef;
}

.events-container {
  position: relative;
  width: 100%;
}

.time-grid-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background-color: #eee;
  z-index: 1;
}

.current-time-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #dc3545;
  z-index: 20;
  box-shadow: 0 1px 3px rgba(220, 53, 69, 0.3);
}

.current-time-indicator {
  position: absolute;
  left: -6px;
  top: -4px;
  width: 12px;
  height: 12px;
  background-color: #dc3545;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.event-item {
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
}

.event-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.event-content {
  padding: 8px 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
}

.event-title {
  font-weight: 600;
  font-size: 13px;
  color: white;
  line-height: 1.2;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-type {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-status {
  font-weight: 400;
  font-style: italic;
  opacity: 0.9;
}

.event-time {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.9);
  margin-top: auto;
}

.event-menu-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 14px;
  padding: 2px 4px;
  border-radius: 2px;
}

.event-menu-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Event type colors */
.event-type-ORDER_REPORT_SAME_DAY {
  background-color: #007bff;
}

.event-type-ORDER_REPORT_NEXT_DAY {
  background-color: #2de658;
}

.event-type-ORDER_REPORT_V_DAY {
  background-color: #06b0f3;
}

.event-type-ORDER_REPORT_MAXIMUM {
  background-color: #2379fa;
}

.event-type-NOT_PULLED_REPORT {
  background-color: #dc3545;
}

.event-type-NOT_SENT_REPORT {
  background-color: #ce35dc;
}

.event-type-ERROR_REPORT {
  background-color: #f10606;
}

.event-inactive {
  opacity: 0.6;
}

.event-disabled {
  background-color: #7a7e81 !important;
  opacity: 0.8;
}

.event-disabled .event-title,
.event-disabled .event-type,
.event-disabled .event-status,
.event-disabled .event-time {
  color: rgba(255, 255, 255, 0.8);
}

/* Loading overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* Event menu */
.event-menu {
  position: fixed;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 99999;
  font-size: 14px;
  min-width: 120px;
}

.event-menu div {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
}

.event-menu div:last-child {
  border-bottom: none;
}

.event-menu div:hover {
  background-color: #f8f9fa;
}

.text-danger {
  color: #dc3545;
}

/* Event form modal */
.event-form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.event-form-container {
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 1000px;
  max-width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-row .col {
  flex: 1;
}

/* Edit form specific styles */
.edit-form {
  width: 1000px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
}

.form-card {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.report-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.report-label span {
  font-weight: 400;
  color: #6c757d;
  font-size: 0.9em;
}

.input-height {
  height: 38px;
}

.upper {
  text-transform: uppercase;
}

.input-box {
  min-height: 20px;
}

.input-alert {
  color: #dc3545;
  font-size: 0.875em;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.checkbox-input {
  margin-right: 8px;
  margin-top: 0;
  vertical-align: middle;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-label {
  margin-bottom: 0;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  line-height: 1.2;
}

.form-check {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.form-check-input {
  margin-right: 8px;
  margin-top: 0;
  vertical-align: middle;
}

.form-check-label {
  margin-bottom: 0;
  font-size: 14px;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.report-type-name-grid {
  display: grid !important;
  grid-template-columns: 1fr 1fr !important;
  gap: 15px !important;
}

.report-type-col,
.report-name-col {
  min-width: 0 !important;
}

@media (max-width: 575px) {
  .report-type-name-grid {
    grid-template-columns: 1fr !important;
  }
}

.delivery-time-tag-grid {
  display: grid !important;
  grid-template-columns: 1fr 1fr !important;
  gap: 15px !important;
}

.delivery-time-col,
.skipped-tag-col {
  min-width: 0 !important;
}

@media (max-width: 575px) {
  .delivery-time-tag-grid {
    grid-template-columns: 1fr !important;
  }
}

.date-delivery-grid {
  display: grid !important;
  grid-template-columns: 1fr 1fr !important;
  gap: 15px !important;
}

.date-col,
.delivery-date-col {
  min-width: 0 !important;
}

@media (max-width: 575px) {
  .date-delivery-grid {
    grid-template-columns: 1fr !important;
  }
}

.status-notes-grid {
  display: grid !important;
  grid-template-columns: 1fr 2fr !important;
  gap: 15px !important;
}

.status-col,
.notes-col {
  min-width: 0 !important;
}

@media (max-width: 575px) {
  .status-notes-grid {
    grid-template-columns: 1fr !important;
  }
}
</style>
